import React, { useEffect, useState } from "react";
import { Button } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { connect } from "react-redux";

import { thereWasAProblem } from "@eave/shared/utils";
import { MaritalStatusEnum, Vesting, VestingTypeEnum } from "@eave/shared/types/api/vesting";

import Spinner from "web_modules/shared/Spinner";
import LabeledInput from "web_modules/LabeledInput";

import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import { loadApp } from "underwriter_dashboard/actions";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import WorksheetEditor from "web_modules/underwriter_dashboard/shared/WorksheetEditor";

import { createVesting } from "underwriter_dashboard/hurdle_editors/Vesting/api";
import PowerOfAttorneys from "./PowerOfAttorneys";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

require(`../../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/\
vesting.scss`);

const isVestingTypeInvisible = (type: string) =>
  type === VestingTypeEnum.NoVestingType || type === VestingTypeEnum.Individual;

const isMaritalStatusInvisible = (maritalStatus: string) =>
  maritalStatus === MaritalStatusEnum.NoMaritalStatusOption;

const getFullName = (names: string[]) => names.filter(Boolean).join(" ").trim();

interface VestingProps extends HurdleProps {
  dispatch: any;
}

const VestingEditor: React.FC<VestingProps> = ({ app, hurdle, onHide, dispatch }) => {
  const [vesting, setVesting] = useState<Vesting>({
    type: app.vesting_type,
    toRead: app.vesting_to_read,
    toReadSuggested: app.vesting_to_read,
    maritalStatus: app.marital_status,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const borrowerNames = Object.values(app.borrowers)
      .map(b => getFullName([b.first_name, b.middle_name, b.last_name, b.suffix]))
      .join(" and ");

    let vestingLanguage = "";
    if (vesting.type && !isVestingTypeInvisible(vesting.type)) {
      vestingLanguage += `, ${vesting.type.charAt(0).toLowerCase() + vesting.type.slice(1)}`;
    }

    let maritalStatusLanguage = "";
    if (vesting.maritalStatus && !isMaritalStatusInvisible(vesting.maritalStatus)) {
      maritalStatusLanguage += `, ${
        vesting.maritalStatus.charAt(0).toLowerCase() + vesting.maritalStatus.slice(1)
      }`;
    }

    setVesting({
      ...vesting,
      toReadSuggested: `${borrowerNames}${maritalStatusLanguage}${vestingLanguage}`,
    });
  }, [vesting.type, vesting.maritalStatus]);

  const updateVestingToReadWithSuggestedVesting = () => {
    setVesting({
      ...vesting,
      toRead: vesting.toReadSuggested,
    });
  };

  const onSave = () => {
    setIsLoading(true);

    const id = app.poa[0]?.id;
    const itemsToSave = [];

    if (vesting.type !== "" || vesting.toRead !== "" || vesting.maritalStatus !== "") {
      itemsToSave.push(createVesting(app.id, vesting));
    }

    Promise.all(itemsToSave)
      .catch(e => {
        thereWasAProblem("saving vesting");
        throw e;
      })
      .finally(() => {
        setIsLoading(false);
        loadApp(dispatch, app.id);
      });
  };

  const handleVestingType = e => setVesting({ ...vesting, type: e ? e.value : "" });
  const handleMaritalStatus = e => setVesting({ ...vesting, maritalStatus: e ? e.value : "" });

  const handleToRead = e => setVesting({ ...vesting, toRead: e.target.value });

  if (isLoading) {
    return <Spinner active />;
  }

  const hasCoborrower = !!app.borrower2_ssn;

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      <div className="dashboard-container vesting-hurdle">
        <div className="hurdles-editor__group">
          <h4 className="hurdles-editor__group-name">Borrower Information</h4>
          <table className="table hurdles-editor__table">
            <thead>
              <tr>
                <th className="col-lg-2" />
                <th className="col-lg-2">Name</th>
                <th className="col-lg-2">Marital Status</th>
                {hasCoborrower && <th className="col-lg-2">Relationship to co-borrower</th>}
                <th className="col-lg-2">Relation to Subject Property</th>
                <th className="col-lg-2">Will Occupy Subject Property</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Borrower1</td>
                <td>
                  {getFullName([
                    app.borrower1_first_name,
                    app.borrower1_middle_name,
                    app.borrower1_last_name,
                    app.borrower1_suffix,
                  ])}
                </td>
                <td>{app.borrower1_relationship_status}</td>
                {hasCoborrower && (
                  <td>
                    {
                      /* should be borrower2 :) since only defined for borrower2 */
                      app.borrower2_relationship
                    }
                  </td>
                )}
                <td>
                  {/* When handling refis, this could also be 'Current Owner' */}
                  Proposed Owner
                </td>
                <td>
                  {/* TODO (mike) Are we handling non-occupying co-borrowers? */}
                  Occupy
                </td>
              </tr>

              {hasCoborrower && (
                <tr>
                  <td>Borrower2</td>
                  <td>
                    {getFullName([
                      app.borrower2_first_name,
                      app.borrower2_middle_name,
                      app.borrower2_last_name,
                      app.borrower2_suffix,
                    ])}
                  </td>
                  <td>{app.borrower2_relationship_status}</td>
                  <td>{app.borrower2_relationship}</td>
                  <td>Proposed Owner</td>
                  <td>Occupy</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="vesting-information">
          <h4 className="hurdles-editor__group-name">Vesting Information</h4>
          <LabeledInput
            inputType="text"
            inputName="vesting_to_read"
            labelValue="Vesting to Read"
            inputValue={vesting.toRead}
            onChange={e => handleToRead(e)}
          />
          <LabeledInput
            inputType="select"
            inputName="marital_status"
            labelValue="Marital status"
            inputValue={vesting.maritalStatus}
            options={Object.values(MaritalStatusEnum).map(v => ({ label: v, value: v }))}
            onChange={e => handleMaritalStatus(e)}
          />
          <LabeledInput
            inputType="select"
            inputName="vesting_type"
            labelValue="Vesting Type"
            inputValue={vesting.type}
            options={Object.values(VestingTypeEnum).map(v => ({ label: v, value: v }))}
            onChange={e => handleVestingType(e)}
          />
        </div>

        <div className="title_only_borrower">
          <WorksheetEditor
            appId={app.id}
            loanFileWsId={app.loan_file_worksheet_id}
            openToWsName="miscellaneous"
            hideNavigation
          />
        </div>

        <div className="vesting-information-suggested">
          <LabeledInput
            disabled
            inputType="text"
            inputName="vesting_to_read_suggested"
            labelValue="Suggested Vesting to Read"
            inputValue={vesting.toReadSuggested}
          />
        </div>
        <Button
          className="vesting-information-populate"
          onClick={updateVestingToReadWithSuggestedVesting}
        >
          Populate vesting to read with suggested
        </Button>

        <div className="power-of-attorneys">
          <PowerOfAttorneys app={app} powerOfAttorneys={app.poa} />
        </div>

        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button onClick={onSave} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      </div>
    </HurdleView>
  );
};

export default connect()(VestingEditor);
